import type { ReactNode } from 'react'
import type { PrimitiveTableColumn } from '@pbt/pbt-ui-components'

import i18n from '~/locales/i18n'

import AppointmentEventTypeFilter from './AppointmentEventTypeFilter'
import AppointmentTypeClientSchedulingFilter from './AppointmentTypeClientSchedulingFilter'
import AppointmentTypeColorFilter from './AppointmentTypeColorFilter'
import AppointmentTypeDropOffFilter from './AppointmentTypeDropOffFilter'
import AppointmentTypeEnabledFilter from './AppointmentTypeEnabledFilter'
import HeaderCellWithTooltip from './HeaderCellWithTooltip'
import { Filters, Sortings } from './utils'

/** Column type allowing ReactNode labels for tooltip-wrapped headers. */
type AppointmentConfigurationColumn = Omit<PrimitiveTableColumn, 'label'> & {
  label: ReactNode
}

/**
 * V2 layout: 6 columns with drop-off, HeaderCellWithTooltip, 12-unit grid.
 * Used when APPOINTMENT_CONFIG_LAYOUT_V2 is on. V2 always includes drop-off.
 *
 * Layout: [Color block: 1/12] [Other columns block: 11/12]
 * getColumnStyles uses (width/12)*100%, so "other" columns must sum to 12.
 */
const COLUMN_WIDTHS = {
  COLOR: 1,
  TYPE: 2,
  DISPLAY_NAME: 2.75,
  DURATION: 1.5,
  DROP_OFF: 1.25,
  CLIENT_SCHEDULING: 2,
  ACTIVE: 1,
} as const

const withHeaderTooltip = (label: string, sublabel?: string) => (
  <HeaderCellWithTooltip label={label} sublabel={sublabel}>
    {label}
  </HeaderCellWithTooltip>
)

export const getAppointmentConfigurationColumns =
  (): PrimitiveTableColumn[] => {
    const baseColumns: AppointmentConfigurationColumn[] = [
      {
        label: withHeaderTooltip(i18n.t('Common:COLOR')),
        filter: Filters.COLOR,
        FilterComponent: AppointmentTypeColorFilter,
        width: COLUMN_WIDTHS.COLOR,
      },
      {
        label: withHeaderTooltip(i18n.t('Common:TYPE_ONE')),
        filter: Filters.EVENT_TYPE_IDS,
        FilterComponent: AppointmentEventTypeFilter,
        width: COLUMN_WIDTHS.TYPE,
      },
      {
        label: withHeaderTooltip(i18n.t('Common:DISPLAY_NAME')),
        prop: 'procedureShortDescription',
        width: COLUMN_WIDTHS.DISPLAY_NAME,
        sorting: Sortings.ORDER_ONLY_BY_NAME_ASCENDING,
      },
      {
        label: withHeaderTooltip(
          i18n.t('Common:DURATION'),
          i18n.t('Time:LABEL.HOURS_MINUTES'),
        ),
        sublabel: i18n.t('Time:LABEL.HOURS_MINUTES'),
        width: COLUMN_WIDTHS.DURATION,
      },
      {
        label: withHeaderTooltip(i18n.t('Common:DROP_OFF')),
        filter: Filters.DROP_OFF,
        FilterComponent: AppointmentTypeDropOffFilter,
        width: COLUMN_WIDTHS.DROP_OFF,
      },
      {
        label: withHeaderTooltip(i18n.t('Common:CLIENT_SCHEDULING')),
        filter: Filters.ONLINE_SCHEDULING_ENABLED,
        FilterComponent: AppointmentTypeClientSchedulingFilter,
        width: COLUMN_WIDTHS.CLIENT_SCHEDULING,
      },
      {
        label: withHeaderTooltip(i18n.t('Common:ACTIVE_ONE')),
        filter: Filters.ENABLED,
        FilterComponent: AppointmentTypeEnabledFilter,
        width: COLUMN_WIDTHS.ACTIVE,
      },
    ]

    const otherColumns = baseColumns.slice(1)
    const otherTotal = otherColumns.reduce(
      (sum, col) => sum + (col.width ?? 0),
      0,
    )
    const targetTotal = 12
    const scaleFactor = targetTotal / otherTotal

    const scaledColumns = otherColumns.map((col) => ({
      ...col,
      width: Math.round((col.width ?? 0) * scaleFactor * 100) / 100,
    }))

    const sumExceptLast = scaledColumns
      .slice(0, -1)
      .reduce((s, col) => s + (col.width ?? 0), 0)
    if (scaledColumns.length > 0) {
      const lastCol = scaledColumns[scaledColumns.length - 1]
      scaledColumns[scaledColumns.length - 1] = {
        ...lastCol,
        width: Math.round((targetTotal - sumExceptLast) * 100) / 100,
      }
    }

    const result: AppointmentConfigurationColumn[] = [
      baseColumns[0],
      ...scaledColumns,
    ]
    // Cast: PrimitiveTableColumn.label is string, but FilterLabel renders ReactNode.
    // getColumnKey uses index for uniqueness so ReactNode labels work at runtime.
    return result as PrimitiveTableColumn[]
  }
